/**
 * Data layer for `custom-currency-per-invoice`. Thin typed fetch wrappers over /api/v1/custom-currency-per-invoice.
 * Raw `fetch()` is confined to this file per the dash contract.
 */

export interface CustomCurrencyPerInvoiceRow {
	id: string;
	tenant_id: string;
	created_at: number;
	updated_at: number;
	[key: string]: unknown;
}

export interface ListCustomCurrencyPerInvoiceResponse {
	count: number;
	items: CustomCurrencyPerInvoiceRow[];
}

export interface CreateCustomCurrencyPerInvoiceBody {
	[key: string]: unknown;
}

const BASE = "/api/v1/custom-currency-per-invoice";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function list(): Promise<ListCustomCurrencyPerInvoiceResponse> {
	const res = await fetch(BASE, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ListCustomCurrencyPerInvoiceResponse>(res);
}

export async function create(body: CreateCustomCurrencyPerInvoiceBody): Promise<CustomCurrencyPerInvoiceRow> {
	const res = await fetch(BASE, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body ?? {}),
	});
	return parseOrThrow<CustomCurrencyPerInvoiceRow>(res);
}

export async function remove(id: string): Promise<{ removed: boolean }> {
	const res = await fetch(`${BASE}/${encodeURIComponent(id)}`, {
		method: "DELETE",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<{ removed: boolean }>(res);
}

export const customCurrencyPerInvoiceQueryKeys = {
	all: ["custom-currency-per-invoice"] as const,
	list: () => [...customCurrencyPerInvoiceQueryKeys.all] as const,
	detail: (id: string) => [...customCurrencyPerInvoiceQueryKeys.all, id] as const,
};
